import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ShoppingBag, ChevronDown, UserRound, ExternalLink, Languages } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLocale } from "@/context/LocaleContext";
import { circleNavLabel } from "@/lib/cerclesPaiens";

export const NAV = [
  {
    id: "tambours", label: "Tambours",
    columns: [
      { titre: "Créer ou faire créer", items: [
        { to: "/offres/stage-fabrication-tambour#dates", label: "Stage de création en groupe", desc: "Vous fabriquez votre tambour à l’atelier" },
        { to: "/offres/stage-fabrication-tambour#individuel", label: "Stage individuel ou privatisé", desc: "Vous le fabriquez avec Ludovic, à la date choisie" },
        { to: "/tambours/editions-limitees", label: "Stages d’exception", desc: "Super Lune, Solstice et Tambours des Lieux" },
        { to: "/tambour-sur-mesure", label: "Faire créer mon tambour", desc: "Création artisanale unique par Ludovic, sur devis" },
        { to: "/tambour-pour-structures", label: "Pour les structures", desc: "Cercle ou stage dans vos locaux, sur devis" },
      ] },
      { titre: "Voir les tambours", items: [
        { to: "/boutique", label: "Tambours disponibles", desc: "Pièces uniques actuellement en boutique" },
        { to: "/tambours-realises", label: "Galerie · réalisations", desc: "Créations de Ludovic, déjà réalisées" },
      ] },
    ],
  },
  {
    id: "accompagnements", label: "Consultations & soins",
    columns: [
      { titre: "Consultations", items: [
        { to: "/offres/consultations-holistiques", label: "Consultations", desc: "Augure · Guidance · Médiumnité" },
      ] },
      { titre: "Soins & accompagnements", items: [
        { to: "/offres/soins-energetiques", label: "Soins énergétiques", desc: "À distance, sans rendez-vous" },
        { to: "/offres/soins-energetiques#accompagnements-28", label: "Accompagnements 28 jours", desc: "Healing, rétablissement physique" },
        { to: "/soin", label: "Soin du moment", desc: "Soin collectif à distance — sabbats & pleines lunes" },
      ] },
    ],
  },
  {
    id: "geobiologie", label: "Géobiologie",
    columns: [
      { titre: "Intervenir sur un lieu", items: [
        { to: "/offres/lieux-habitats", label: "Géobiologie · harmonisation", desc: "Comprendre, nettoyer et harmoniser un lieu" },
        { to: "/offres/lieux-habitats#tarifs", label: "Tarifs", desc: "Barème selon la taille du lieu" },
        { to: "/offres/lieux-habitats#visite", label: "Demande d’intervention", desc: "Réserver une visite ou demander un devis" },
      ] },
    ],
  },
  {
    id: "formations", label: "Activités & formations",
    columns: [
      { titre: "Pratiquer", items: [
        { to: "/offres/cercles-evenements", label: "Cercles de tambours", desc: "Rencontres au fil des saisons" },
        { to: "/offres/lieux-habitats#randonnee", label: "Randonnées cosmo-telluriques", desc: "Expériences de terrain en petit groupe" },
        { to: "/offres/lieux-habitats#mystere-caquelon", label: "Mystère & caquelon", desc: "Les soirées gourmandes d’Entre Les Mondes" },
        { to: "/offres/lieux-habitats#soirees-decouverte", label: "Initiations géobiologie · radiesthésie", desc: "Soirées découverte à l’atelier" },
      ] },
      { titre: "Se former", items: [
        { to: "/le-wyrd", label: "Le Wyrd", desc: "Parcours de six mois autour du tambour" },
        { to: "/formation", label: "La Toile du Wyrd", desc: "Espace de formation privé" },
        { to: "/le-wyrd#wyrd-dates", label: "Formations tambour", desc: "Cercles, journées et rituels du parcours" },
      ] },
      { titre: "Voyager & rencontrer", items: [
        { to: "/agenda?type=voyages", label: "Voyages", desc: "Activités existantes en Irlande" },
        { to: "/agenda?type=salons", label: "Marchés & salons", desc: "Retrouver Entre Les Mondes en événement" },
      ] },
    ],
  },
  { id: "agenda", label: "Agenda", to: "/agenda" },
  {
    id: "a-propos", label: "À propos",
    columns: [
      { titre: "Ludovic & la démarche", items: [
        { to: "/a-propos", label: "Ludovic · présentation", desc: "Parcours et histoire d’Entre Les Mondes" },
        { to: "/a-propos#demarche", label: "La démarche", desc: "Une approche simple, incarnée et sans dogme" },
      ] },
      { titre: "Lire & écouter", items: [
        { to: "/le-carnet-des-mondes", label: "Le Carnet des Mondes", desc: "Publication numérique · N°1" },
        { to: "/blog", label: "Blog", desc: "Guides, tambours et pratiques" },
        { to: "/a-ecouter", label: "Podcast · à écouter", desc: "L’Esprit de Dagaz et playlist" },
      ] },
    ],
  },
];

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const groupItems = (g) => g.items || g.columns.flatMap((c) => c.items);
const isActive = (group, pathname) => groupItems(group).some((i) => { if (!i.to) return false; const p = i.to.split(/[?#]/)[0]; return pathname === p || (p !== "/" && p !== "/contact" && pathname.startsWith(p + "/")); });

const Logo = () => {
  const { t } = useLocale();
  return (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
    <img src="/logo.png" alt="Entre Les Mondes" className="w-10 h-10 object-contain transition-transform duration-700 group-hover:rotate-[120deg]" />
    <span className="leading-none">
      <span className="block font-eccentric text-xl leading-none tracking-tight whitespace-nowrap" data-testid="header-wordmark">Entre Les Mondes</span>
      <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-fumee mt-1">{t("Orbe · Suisse")}</span>
    </span>
  </Link>
  );
};

const MenuItem = ({ s, group, onClick, compact = false }) => {
  const { t } = useLocale();
  const cls = `group rounded-xl px-3 hover:bg-sand-dark/60 transition-colors duration-300 ${compact ? "py-1.5" : "py-2.5"}`;
  const inner = (<>
    <span className="block text-sm text-bark font-medium group-hover:text-terra transition-colors">{t(s.label)}{s.href && <ExternalLink className="inline w-3 h-3 ml-1.5 -mt-0.5 text-fumee" />}</span>
    {s.desc && <span className="block text-xs text-fumee mt-0.5 leading-snug">{t(s.desc)}</span>}
  </>);
  const tid = `menu-${group.id}-${slug(s.label)}`;
  return s.href ? <a href={s.href} target="_blank" rel="noopener noreferrer" onClick={onClick} data-testid={tid} className={cls}>{inner}</a>
    : <Link to={s.to} onClick={onClick} data-testid={tid} className={cls}>{inner}</Link>;
};

const MobileGroup = ({ g, open, onToggle, onNavigate }) => (
  <MobileGroupContent g={g} open={open} onToggle={onToggle} onNavigate={onNavigate} />
);

const MobileGroupContent = ({ g, open, onToggle, onNavigate }) => {
  const { t } = useLocale();
  return (
  <div data-testid={`mobile-group-${g.id}`}>
    <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`mobile-nav-toggle-${g.id}`}
      className={`w-full flex items-center justify-between font-serif text-3xl py-3 text-left transition-colors duration-300 ${open ? "text-terra" : "hover:text-terra"}`}>
      {t(g.label)} <ChevronDown className={`w-5 h-5 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 pb-4 pl-1">
            {g.columns.map((c) => (
              <div key={c.titre} data-testid={`mobile-nav-col-${slug(c.titre)}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra mb-1.5">{t(c.titre)}</p>
                <div className="flex flex-col gap-0.5">
                  {c.items.map((s) => s.href
                    ? <a key={s.href} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`mobile-nav-${slug(s.label)}`} className="font-serif text-xl py-0.5 hover:text-terra transition-colors duration-300 inline-flex items-center gap-2">{t(s.label)} <ExternalLink className="w-3.5 h-3.5 text-sand/50" /></a>
                    : <NavLink key={s.to} to={s.to} onClick={onNavigate} data-testid={`mobile-nav-${slug(s.label)}`} className="font-serif text-xl py-0.5 hover:text-terra transition-colors duration-300">{t(s.label)}</NavLink>)}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
  );
};

const Dropdown = ({ group, active }) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const triggerRef = useRef(null);
  const columnCount = group.columns?.length || 1;
  const [menuPosition, setMenuPosition] = useState({ left: 24, width: columnCount * 232 });
  const positionMenu = useCallback(() => {
    if (!triggerRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const width = Math.min(columnCount * 232, window.innerWidth - 48);
    const left = Math.min(Math.max(trigger.left, 24), window.innerWidth - width - 24);
    setMenuPosition({ left, width });
  }, [columnCount]);
  useEffect(() => {
    if (!open || !group.columns) return undefined;
    positionMenu();
    window.addEventListener("resize", positionMenu);
    return () => window.removeEventListener("resize", positionMenu);
  }, [group.columns, open, positionMenu]);
  const show = () => { clearTimeout(timer.current); positionMenu(); setOpen(true); };
  const hide = () => { timer.current = setTimeout(() => setOpen(false), 120); };
  return (
    <div ref={triggerRef} className="relative" onMouseEnter={show} onMouseLeave={hide}>
      {group.to ? (
        <Link to={group.to} onClick={() => setOpen(false)} aria-expanded={open} aria-haspopup="true" data-testid={`nav-${group.id}`}
          className={`inline-flex items-center gap-1.5 text-[13px] tracking-wide whitespace-nowrap transition-colors duration-300 ${active || open ? "text-terra" : "text-bark/80 hover:text-bark"}`}>
          {t(group.label)} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </Link>
      ) : (
      <button type="button" onClick={() => { positionMenu(); setOpen((o) => !o); }} aria-expanded={open} aria-haspopup="true" data-testid={`nav-${group.id}`}
        className={`inline-flex items-center gap-1.5 text-[13px] tracking-wide whitespace-nowrap transition-colors duration-300 ${active || open ? "text-terra" : "text-bark/80 hover:text-bark"}`}>
        {t(group.label)} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      )}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className={group.columns ? "fixed top-20 pt-3 pointer-events-none" : "absolute left-0 top-full pt-5 w-[22rem]"} style={group.columns ? menuPosition : undefined} data-testid={`menu-${group.id}`}>
            <div className={`pointer-events-auto bg-sand border border-sand-dark rounded-2xl shadow-2xl shadow-earth/15 p-2 ${group.columns ? "grid w-full gap-1 p-4" : "flex flex-col gap-0.5"}`}
              style={group.columns ? { gridTemplateColumns: `repeat(${group.columns.length}, minmax(0, 1fr))` } : undefined}>
              {group.columns ? group.columns.map((c) => (
                <div key={c.titre} className="flex flex-col gap-0.5" data-testid={`menu-${group.id}-col-${slug(c.titre)}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra px-3 pt-2 pb-2">{t(c.titre)}</p>
                  {c.items.map((s) => <MenuItem key={s.to || s.href} s={s} group={group} onClick={() => setOpen(false)} compact={!s.desc} />)}
                </div>
              )) : group.items.map((s) => <MenuItem key={s.to} s={s} group={group} onClick={() => setOpen(false)} />)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const { count } = useCart();
  const { locale, t, localizePath, switchLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navigation = NAV.map((group) => group.id !== "formations" ? group : { ...group, columns: group.columns.map((column) => ({ ...column, items: column.items.map((item) => item.to === "/offres/cercles-evenements" ? { ...item, label: circleNavLabel(locale), desc: locale === "en" ? "Living traditions, ritual gatherings and drums" : "Traditions vivantes, rencontres rituelles et tambours" } : item) })) });

  useEffect(() => { setOpen(false); setMobileGroup(null); }, [pathname]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-xl bg-sand/85 border-b border-sand-dark/80">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Logo />
          <nav className="hidden xl:flex items-center gap-4 2xl:gap-5" aria-label="Navigation principale">
            {navigation.map((g) => g.items || g.columns ? <Dropdown key={g.id} group={g} active={isActive(g, pathname)} /> : (
              <NavLink key={g.id} to={g.to} end={g.to === "/"} data-testid={`nav-${g.id}`}
                className={({ isActive: a }) => `link-line text-[13px] tracking-wide whitespace-nowrap transition-colors duration-300 ${a ? "text-terra" : "text-bark/80 hover:text-bark"}`}>
                {t(g.label)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/compte" data-testid="header-compte-link" aria-label={t("Mon espace")} title={t("Mon espace")}
              className="p-2.5 rounded-full border border-wood/20 hover:border-terra hover:text-terra transition-colors duration-300">
              <UserRound className="w-4 h-4" />
            </Link>
            <button onClick={() => navigate(localizePath("/panier"))} data-testid="header-cart-button" aria-label={locale === "en" ? "View cart" : "Voir le panier"}
              className="relative p-2.5 rounded-full border border-wood/20 hover:border-terra hover:text-terra transition-colors duration-300">
              <ShoppingBag className="w-4 h-4" />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-terra text-earth text-[10px] font-mono flex items-center justify-center">{count}</span>
              )}
            </button>
            <Link to="/contact" data-testid="header-contact-cta"
              className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full bg-earth text-sand text-sm tracking-wide hover:bg-terra hover:text-earth transition-colors duration-300">
              {t("Contact")}
            </Link>
            <div className="hidden sm:flex items-center border border-wood/20 rounded-full p-0.5" data-testid="language-selector">
              <button type="button" onClick={() => switchLocale("fr")} data-testid="language-fr-button" aria-pressed={locale === "fr"} className={`px-2 py-1 rounded-full text-[10px] font-mono ${locale === "fr" ? "bg-earth text-sand" : "text-fumee hover:text-terra"}`}>FR</button>
              <button type="button" onClick={() => switchLocale("en")} data-testid="language-en-button" aria-pressed={locale === "en"} className={`px-2 py-1 rounded-full text-[10px] font-mono ${locale === "en" ? "bg-earth text-sand" : "text-fumee hover:text-terra"}`}>EN</button>
            </div>
            <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label={t("Ouvrir le menu")} className="xl:hidden p-2.5 rounded-full border border-wood/20">
              <Menu className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-earth grain text-sand flex flex-col overflow-y-auto" data-testid="mobile-menu">
            <div className="flex items-center justify-between px-6 h-20 shrink-0">
              <Link to="/" onClick={() => setOpen(false)} className="font-eccentric text-2xl" data-testid="mobile-logo-link">Entre Les Mondes</Link>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label={t("Fermer le menu")} className="p-2.5 rounded-full border border-sand/25">
                <X className="w-4 h-4" />
              </button>
            </div>
            <nav className="flex-1 px-8 py-4 flex flex-col gap-2" aria-label="Menu mobile">
              {navigation.map((g, gi) => (
                <motion.div key={g.id} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * gi, duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="border-b border-sand/10 pb-2">
                  {g.columns ? <MobileGroup g={g} open={mobileGroup === g.id} onToggle={() => setMobileGroup(mobileGroup === g.id ? null : g.id)} onNavigate={() => setOpen(false)} />
                    : <NavLink to={g.to} end={g.to === "/"} onClick={() => setOpen(false)} data-testid={`mobile-nav-${g.id}`} className="block font-serif text-3xl py-3 hover:text-terra transition-colors duration-300">{t(g.label)}</NavLink>}
                </motion.div>
              ))}
              <div className="flex flex-wrap gap-3 pt-4">
                <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-nav-contact" className="inline-flex items-center px-6 py-3 rounded-full bg-terra text-earth text-sm">{t("Contact")}</Link>
                <button type="button" onClick={() => { switchLocale(locale === "en" ? "fr" : "en"); setOpen(false); }} data-testid="mobile-language-toggle" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-sand/25 text-sm"><Languages className="w-4 h-4" />{locale === "en" ? "Français" : "English"}</button>
              </div>
            </nav>
            <div className="px-8 pb-10 font-mono text-xs uppercase tracking-[0.3em] text-sand/50 shrink-0">Orbe · Suisse romande</div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
